import {
  ClaimPaymentOutcome,
  ClaimPaymentResult,
  getClaimPaymentOutcome
} from "./claimPaymentOutcome";
import { updareInvoiceInterface } from "../interfaces/PaymentIntegrities.interfaces";

export interface AdminPaymentLifecycleProjection {
  paymentLifecycleId?: number | null;
  talentPaymentStatus?: string | null;
  // 5.27 FB-5: admin-only field computed by the API from the same predicate
  // that guards POST /v3/invoices/:id/retryPayment.
  automaticPayoutRetryEligible?: boolean | null;
}

export function canConfirmManualTalentPayment(
  payment: AdminPaymentLifecycleProjection | null | undefined
): boolean {
  return Boolean(
    payment &&
      Number(payment.paymentLifecycleId) > 0 &&
      payment.talentPaymentStatus === "action_required"
  );
}

// 5.27 FB-5: offer "Retry automatic payout" only when the API says the server
// retry predicate currently holds. Anything other than a literal `true` (absent,
// false, null, "true", 1) hides it. The server stays the enforcing check.
export function canRetryAutomaticPayout(
  payment: AdminPaymentLifecycleProjection | null | undefined
): boolean {
  return Boolean(
    payment &&
      payment.automaticPayoutRetryEligible === true &&
      Number(payment.paymentLifecycleId) > 0 &&
      payment.talentPaymentStatus === "action_required"
  );
}

export function filterPaymentIntegrityActions(
  actions: string[],
  payment: AdminPaymentLifecycleProjection | null | undefined
): string[] {
  return actions.filter(
    action =>
      action !== "Confirm Manual Talent Payment" ||
      canConfirmManualTalentPayment(payment)
  );
}

export function manualPaymentTargetIsCurrent(
  selectedInvoiceId: number | null | undefined,
  boundInvoiceId: number | null | undefined,
  reference: string
): boolean {
  return Boolean(
    selectedInvoiceId &&
      selectedInvoiceId === boundInvoiceId &&
      reference.trim()
  );
}

export function buildOfficePaidUpdate(invoiceId: number, value: number) {
  return {
    invoiceId,
    dentistMarkedPaid: value
  };
}

interface BoundOfficeChargeDependencies {
  claimPayment: (invoiceId: number) => Promise<ClaimPaymentResult>;
  writeNote: (invoiceId: number, note: string) => Promise<unknown>;
  updateInvoice: (update: updareInvoiceInterface) => Promise<unknown>;
  nudgeJames: (invoiceId: number) => Promise<unknown>;
}

export async function runBoundOfficeChargeAction(
  invoiceId: number,
  resolvedAt: string,
  dependencies: BoundOfficeChargeDependencies
): Promise<ClaimPaymentOutcome> {
  const payment = await dependencies.claimPayment(invoiceId);
  const outcome = getClaimPaymentOutcome(payment);
  const invoiceUpdate: updareInvoiceInterface = {
    invoiceId,
    dentistMarkedPaid: 1
  };
  if (outcome.shouldResolve) {
    invoiceUpdate.resolved = resolvedAt;
  }

  // The API has already confirmed the office charge. A failed note, invoice
  // update, or reminder must never turn that confirmation into "Payment failed"
  // and encourage a second charge or a replacement manual payment.
  const followups = [
    () => dependencies.writeNote(invoiceId, outcome.note),
    () => dependencies.updateInvoice(invoiceUpdate),
    () => dependencies.nudgeJames(invoiceId)
  ];
  const completed = await Promise.all(
    followups.map(work =>
      Promise.resolve()
        .then(work)
        .then(() => true, () => false)
    )
  );
  if (completed.some(success => !success)) {
    return {
      ...outcome,
      shouldResolve: outcome.shouldResolve && completed[1],
      requiresAction: true,
      message:
        `${outcome.message}. Administrative follow-up could not be completed. ` +
        "Do not charge again or send a replacement payment; refresh and contact Support."
    };
  }
  return outcome;
}

interface BoundManualPaymentDependencies {
  confirmManualPayment: (
    invoiceId: number,
    reference: string
  ) => Promise<{ talentPaymentStatus?: string | null }>;
  writeNote: (invoiceId: number, note: string) => Promise<unknown>;
  updateInvoice: (update: updareInvoiceInterface) => Promise<unknown>;
}

export interface ManualTalentPaymentOutcome {
  confirmed: boolean;
  shouldResolve: boolean;
  requiresAction: boolean;
  message: string;
}

const uncertainManualPaymentMessage =
  "Manual Talent payment outcome could not be confirmed. Do not pay again or send a replacement payment; refresh and contact Support.";

export async function runBoundManualTalentPaymentAction(
  invoiceId: number,
  reference: string,
  resolvedAt: string,
  dependencies: BoundManualPaymentDependencies
): Promise<ManualTalentPaymentOutcome> {
  let payment: { talentPaymentStatus?: string | null };
  try {
    payment = await dependencies.confirmManualPayment(invoiceId, reference);
  } catch (_) {
    // A lost response cannot establish whether the external/manual payment was
    // recorded. Never invite a second payment from this uncertainty.
    return {
      confirmed: false,
      shouldResolve: false,
      requiresAction: true,
      message: uncertainManualPaymentMessage
    };
  }
  if (payment?.talentPaymentStatus !== "paid") {
    return {
      confirmed: false,
      shouldResolve: false,
      requiresAction: true,
      message: uncertainManualPaymentMessage
    };
  }

  // The API has confirmed Talent paid. Bookkeeping failures must not turn
  // that financial outcome into an apparent failed payment.
  const [noteSaved, invoiceUpdated] = await Promise.all([
    Promise.resolve()
      .then(() =>
        dependencies.writeNote(
          invoiceId,
          `manual talent payment confirmed: ${reference}`
        )
      )
      .then(() => true, () => false),
    Promise.resolve()
      .then(() => dependencies.updateInvoice({ invoiceId, resolved: resolvedAt }))
      .then(() => true, () => false)
  ]);

  if (!noteSaved || !invoiceUpdated) {
    return {
      confirmed: true,
      shouldResolve: invoiceUpdated,
      requiresAction: true,
      message:
        "Manual Talent payment confirmed, but administrative follow-up could not be completed. " +
        "Do not pay again or send a replacement payment; refresh and contact Support."
    };
  }
  return {
    confirmed: true,
    shouldResolve: true,
    requiresAction: false,
    message: "Manual Talent payment confirmed and audited"
  };
}

export interface AutomaticPayoutRetryResult {
  httpStatus?: number;
  paymentLifecycleId?: number | null;
  officePaymentStatus?: string | null;
  talentPaymentStatus?: string | null;
  talentPaymentMethod?: string | null;
}

interface BoundAutomaticPayoutRetryDependencies {
  retryAutomaticPayout: (
    invoiceId: number
  ) => Promise<AutomaticPayoutRetryResult>;
  writeNote: (invoiceId: number, note: string) => Promise<unknown>;
}

export interface AutomaticPayoutRetryOutcome {
  retried: boolean;
  requiresAction: boolean;
  message: string;
}

const uncertainAutomaticPayoutRetryMessage =
  "Do not pay this Talent manually or send a replacement payment; refresh and contact Support.";

function errorMessageOf(error: unknown): string {
  const message = error instanceof Error ? error.message : "";
  return message.trim().replace(/\.+$/, "") || "No response from the server";
}

export async function runBoundAutomaticPayoutRetryAction(
  invoiceId: number,
  dependencies: BoundAutomaticPayoutRetryDependencies
): Promise<AutomaticPayoutRetryOutcome> {
  let result: AutomaticPayoutRetryResult;
  try {
    result = await dependencies.retryAutomaticPayout(invoiceId);
  } catch (error) {
    // The API resets the lifecycle before it contacts Stripe, so an error or a
    // lost response cannot prove that no transfer or payout started. Show the
    // server's reason, but never invite a manual or replacement payment.
    return {
      retried: false,
      requiresAction: true,
      message:
        `Automatic payout retry was not confirmed: ${errorMessageOf(error)}. ` +
        uncertainAutomaticPayoutRetryMessage
    };
  }

  const status = result?.talentPaymentStatus;
  let outcome: AutomaticPayoutRetryOutcome & { note: string };
  if (status === "paid") {
    outcome = {
      retried: true,
      requiresAction: false,
      message: "Automatic payout retried; Talent payout is complete",
      note: "automatic payout retry: talent payout complete"
    };
  } else if (status === "processing") {
    outcome = {
      retried: true,
      requiresAction: false,
      message:
        "Automatic payout retried; Talent payout is processing. Do not pay this Talent manually.",
      note: "automatic payout retry: talent payout processing"
    };
  } else if (status === "action_required") {
    // The retry ran, and the lifecycle re-applied its payout gate (for
    // example the account's payout schedule is still not converted).
    outcome = {
      retried: true,
      requiresAction: true,
      message:
        "Automatic payout retried, but the Talent payout still needs action. " +
        "Review the refreshed record before any other payment action.",
      note: "automatic payout retry: talent payout still needs action"
    };
  } else {
    return {
      retried: false,
      requiresAction: true,
      message:
        "Automatic payout retry outcome could not be confirmed. " +
        uncertainAutomaticPayoutRetryMessage
    };
  }

  // The audit note is bookkeeping only. Its failure must not change the
  // financial outcome the API has already reported.
  try {
    await dependencies.writeNote(invoiceId, outcome.note);
  } catch (_) {
    // The API records the retry actor and time on the payment lifecycle.
  }
  return {
    retried: outcome.retried,
    requiresAction: outcome.requiresAction,
    message: outcome.message
  };
}

export function isLifecycleOwnedTalentPaymentMutation(change: {
  tablename?: string | null;
  column?: string | null;
}): boolean {
  return (
    String(change.tablename || "").toLowerCase() === "invoice" &&
    change.column === "hygienistMarkedPaid"
  );
}
